import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import './modal-reserve-v2.scss';
import { useSelector } from 'react-redux';
import useGetReserveDetailsV2 from '../../../api/reserves/useGetReserveDetailV2';
import usePutReserveDetails from '../../../api/reserves/usePutReserveDetails';
import { reserve_status } from '../../../enums/reserve-status.enum';
import Status from '../../../components/agv-status';
import { STATUS_ENUM } from '../../../components/agv-status/status-enum';
import { returnCriticalityDiv } from '../../../utils/criticalities';
import moment from 'moment';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { faFileDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import fleetApi from '../../../services/api.service';
import DOCUMENT_TYPE from '../../../enums/documents.enum';

function getInitials(user) {
  if (!user) return '';
  const first = user.first_name?.[0] || '';
  const last = user.last_name?.[0] || '';
  return (first + last).toUpperCase();
}

function getInitialsDriver(driverFullname) {
  if (!driverFullname) return '';
  const driverFullnameSplitted = driverFullname?.split(' ');
  const first = driverFullnameSplitted[0]?.[0] || '';
  const last = driverFullnameSplitted[1]?.[0] || '';
  return (first + last).toUpperCase();
}

const handleDownload = async (document_id) => {
  try {
    const response = await fleetApi.post(
      `/documents/${document_id}`,
      {},
      {
        responseType: 'blob',
      }
    );

    // Récupération du type de fichier depuis les headers de la réponse
    const contentType = response.headers['content-type'];
    const contentDisposition = response.headers['content-disposition'];

    // Extraction du nom de fichier depuis content-disposition s'il existe
    let filename = `document-${document_id}`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Extension du fichier basée sur le content-type
    const extension = contentType.split('/')[1] || 'jpg';
    if (!filename.includes('.')) {
      filename = `${filename}.${extension}`;
    }

    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // Nettoyage
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error('Erreur lors du téléchargement du document:', error);
  }
};

const renderDocuments = (documents) => {
  if (!documents || documents.length === 0) return null;

  return (
    <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200">
      <div className="tw-flex tw-gap-2">
        {documents.map((document) => (
          <div
            key={document.id}
            className="tw-overflow-hidden tw-rounded-lg tw-h-[120px] tw-w-[96px] tw-relative"
          >
            <img
              src={document.url}
              alt={`Document ${document.id}`}
              className="tw-h-full tw-w-full tw-object-cover tw-object-center"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentNode.innerHTML =
                  '<div class="tw-w-full tw-h-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-text-xs">Image</div>';
              }}
            />
            <div className="tw-absolute tw-top-2 tw-right-2 tw-flex tw-gap-1">
              <a
                href={document.url}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-bg-white tw-rounded-full tw-p-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                <FontAwesomeIcon
                  icon={faEye}
                  className="tw-text-gray-600 tw-w-3 tw-h-3"
                />
              </a>
              <button
                className="tw-bg-white tw-border-none tw-rounded-full tw-p-1.5"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(document.id);
                }}
              >
                <FontAwesomeIcon
                  icon={faFileDownload}
                  className="tw-text-gray-600 tw-w-3 tw-h-3"
                />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function ModalReserveV2({ reserve_id, show, onHide }) {
  const [comment, setComment] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const features = useSelector((state) => state.features);

  const {
    reserve,
    error,
    isLoading,
    refetch: getReserveDetails,
  } = useGetReserveDetailsV2(reserve_id);

  const {
    isSuccess: isSuccessPutReserveDetails,
    error: errorPutReserveDetails,
    isLoading: isLoadingPutReserveDetails,
    putReserveDetails,
  } = usePutReserveDetails();

  useEffect(() => {
    getReserveDetails;
    setComment('');
  }, [reserve_id, isSuccessPutReserveDetails]);

  useEffect(() => {
    if (isSuccessPutReserveDetails) {
      setIsOpen(false);
      onHide(true);
    }
  }, [isSuccessPutReserveDetails]);

  const returnDeliveryDetailsDivOrVoid = () => {
    if (
      reserve?.step?.delivery_details &&
      reserve?.step?.delivery_details?.length > 0 &&
      features?.OTHERS?.BACKOFFICE?.displayDeliveryDetails
    ) {
      return (
        <div className="delivery-details tw-mb-3">
          <div className="delivery-details-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
            Bon de livraison(s)
          </div>
          <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
            {reserve?.step?.delivery_details?.map((deliveryDetail) => (
              <div key={deliveryDetail.delivery_detail_id} className="tw-mb-2">
                <div className="tw-font-medium tw-mb-1">
                  {deliveryDetail.delivery_detail_key}
                </div>
                {deliveryDetail.documents &&
                  deliveryDetail.documents.length > 0 && (
                    <div className="tw-flex tw-flex-col tw-gap-[1px] tw-text-xs tw-text-primary">
                      {deliveryDetail.documents.map((el) => (
                        <div
                          key={`el_${el.id}`}
                          className="tw-relative [text-decoration:underline] tw-tracking-[0.02em]"
                        >
                          <a href={el.url} target="_blank" rel="noreferrer">
                            Télécharger{' '}
                            {el?.doc_type
                              ? DOCUMENT_TYPE.properties[el?.doc_type]?.pronoun
                              : 'la'}{' '}
                            {el?.doc_type
                              ? DOCUMENT_TYPE.properties[el?.doc_type]
                                  ?.display_name
                              : 'DOCUMENT_TYPE'}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return;
  };

  const returnProductsDetailsOrVoid = () => {
    // Récupérer les données
    const items =
      reserve?.step?.delivery_details?.flatMap(
        (deliveryDetail) => deliveryDetail.items
      ) || [];

    const additionalDatas = reserve?.additional_datas;

    // Fonction pour rendre la section produits
    const renderProductsSection = () => {
      if (!reserve?.step?.delivery_details?.length || items?.length === 0) {
        return null;
      }

      return (
        <div className="products tw-mb-3">
          <div className="products-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
            Produits
          </div>
          <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
            {items.map((item) => (
              <div key={item.id}>
                {item.name} ({item.net_weight || item.quantity}{' '}
                {item.weight_unit === 'TONNES' ? 't.' : 'Unité(s)'})
              </div>
            ))}
          </div>
        </div>
      );
    };

    // Fonction pour rendre la section informations produit
    const renderProductInfoSection = () => {
      if (!additionalDatas) {
        return null;
      }

      // Vérifier s'il y a au moins une donnée > 0
      const hasData =
        additionalDatas.nb_bags_missing > 0 ||
        additionalDatas.nb_bags_ripped > 0 ||
        additionalDatas.nb_bags_refused > 0 ||
        additionalDatas.nb_qty_missing > 0 ||
        additionalDatas.nb_qty_refused > 0 ||
        additionalDatas.nb_qty_delivered > 0;

      if (!hasData) {
        return null;
      }

      return (
        <div className="reserve-additional-data tw-mb-3">
          <div className="reserve-additional-data-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
            Informations produit
          </div>
          <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
            {additionalDatas.nb_bags_missing > 0 && (
              <div>Sacs manquants : {additionalDatas.nb_bags_missing}</div>
            )}
            {additionalDatas.nb_bags_ripped > 0 && (
              <div>Sacs déchirés : {additionalDatas.nb_bags_ripped}</div>
            )}
            {additionalDatas.nb_bags_refused > 0 && (
              <div>Sacs refusés : {additionalDatas.nb_bags_refused}</div>
            )}
            {additionalDatas.nb_qty_missing > 0 && (
              <div>
                Quantité VRAC manquante : {additionalDatas.nb_qty_missing}
              </div>
            )}
            {additionalDatas.nb_qty_refused > 0 && (
              <div>
                Quantité VRAC refusée : {additionalDatas.nb_qty_refused}
              </div>
            )}
            {additionalDatas.nb_qty_delivered > 0 && (
              <div>
                Quantité VRAC livrée : {additionalDatas.nb_qty_delivered}
              </div>
            )}
          </div>
        </div>
      );
    };

    // Générer les sections
    const productsSection = renderProductsSection();
    const productInfoSection = renderProductInfoSection();

    // Si aucune des deux sections n'a de contenu, ne rien afficher
    if (!productsSection && !productInfoSection) {
      return null;
    }

    // Retourner le bloc complet "Détails de la livraison"
    return (
      <div className="delivery-details">
        <div className="delivery-details-title tw-text-[15px] tw-font-work tw-font-medium tw-leading-normal tw-tracking-[-0.27px] tw-text-[rgba(0,0,0,0.90)] tw-mb-2">
          Détails de la livraison
        </div>
        {productsSection}
        {productInfoSection}
      </div>
    );
  };

  const returnReserveHeaderTitle = (reserve) => {
    const stringParts = [];
    if (reserve?.created_at) {
      stringParts.push(moment(reserve?.created_at).format('DD/MM/YYYY'));
    }

    if (reserve?.tour?.tour_key) {
      const tourKey = reserve?.tour?.tour_key;
      if (features?.OTHERS?.BACKOFFICE?.tourNumberOnlyLastDigit) {
        stringParts.push(`Tour ${tourKey.slice(-1)}`);
      } else {
        stringParts.push(`Tour ${tourKey}`);
      }
    }

    if (reserve?.vehicle?.vehicle_key) {
      stringParts.push(`Camion ${reserve?.vehicle?.vehicle_key}`);
    }

    if (reserve?.driver?.fullname) {
      stringParts.push(`Chauffeur ${reserve?.driver?.fullname}`);
    }

    return stringParts.join(' • ');
  };

  const returnDestinataireDivIfAny = (reserve) => {
    let addressKeyAndReceiverName = '';
    let fullAddress = '-';

    if (!reserve?.step?.end_address && !reserve?.address) {
      return;
    }

    if (reserve?.step?.end_address) {
      const addressParts = [];
      reserve?.step?.end_address?.address_key
        ? addressParts.push(reserve?.step?.end_address?.address_key)
        : '';
      reserve?.step?.end_address?.name
        ? addressParts.push(reserve?.step?.end_address?.name)
        : '';

      addressKeyAndReceiverName = addressParts.join(' - ');

      fullAddress = reserve?.step?.end_address?.full_address;
    }

    if (reserve?.address) {
      const addressParts = [];
      reserve?.address?.address_key
        ? addressParts.push(reserve?.address?.address_key)
        : '';
      reserve?.address?.name ? addressParts.push(reserve?.address?.name) : '';

      addressKeyAndReceiverName = addressParts.join(' - ');

      fullAddress = reserve?.address?.full_address;
    }

    return (
      <div>
        <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
          Destinataire
        </div>
        {addressKeyAndReceiverName && (
          <div className="receiver-ID tw-mb-3">
            <div className="receiver-ID-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
              Identifiant - Nom et prénom
            </div>
            <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
              {addressKeyAndReceiverName}
            </div>
          </div>
        )}
        <div className="receiver-address tw-mb-3">
          <div className="receiver-address-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
            Adresse de livraison
          </div>
          <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
            {fullAddress}
          </div>
        </div>
      </div>
    );
  };

  const returnCompartmentsAndSilosDivOrVoid = () => {
    let compartmentsAndSilosElements = [];
    if (
      features?.TOUR_MANAGEMENT?.TRUCK_CONTAINERS?.enabled &&
      reserve?.step?.delivery_details &&
      reserve?.step?.delivery_details?.length === 1 &&
      reserve?.step?.delivery_details?.[0]?.items?.length > 0
    ) {
      if (
        reserve?.step?.delivery_details?.[0]?.items &&
        reserve.step.delivery_details[0].items.some(
          (item) => item?.compartments
        )
      ) {
        compartmentsAndSilosElements.push(
          <div key="compartments" className="compartments tw-mb-3">
            <div className="compartments-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
              Compartiments
            </div>
            <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
              {reserve.step.delivery_details[0].items
                ?.filter((item) => item?.compartments)
                ?.map((item) => `${item?.compartments}`)
                ?.join(', ') || '-'}
            </div>
          </div>
        );
      }

      if (
        reserve?.step?.delivery_details?.[0]?.items?.[0]?.additional_data
          ?.silos_to_deliver?.length > 0 ||
        reserve?.step?.delivery_details?.[0]?.items?.[0]?.additional_data
          ?.silos_to_retake?.length > 0
      ) {
        let siloPlannedTitle = 'Silos prévus';
        let silosPlannedDetails =
          'Impossible de récupérer les informations concernant les silos';

        if (
          reserve.step.delivery_details[0].items[0].additional_data
            ?.silos_to_deliver?.length > 0
        ) {
          siloPlannedTitle = 'Silos à livrer prévus';
          silosPlannedDetails =
            reserve.step.delivery_details[0].items[0].additional_data?.silos_to_deliver?.join(
              ', '
            );
        } else if (
          reserve.step.delivery_details[0].items[0].additional_data
            ?.silos_to_retake?.length > 0
        ) {
          siloPlannedTitle = 'Silos à récupérer prévus';
          silosPlannedDetails =
            reserve.step.delivery_details[0].items[0].additional_data?.silos_to_retake?.join(
              ', '
            );
        }

        compartmentsAndSilosElements.push(
          <div
            key="silos-planned"
            className="additional-datas-silos-planned tw-mb-3"
          >
            <div className="additional-datas-silos-planned-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
              {siloPlannedTitle}
            </div>
            <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
              {silosPlannedDetails}
            </div>
          </div>
        );

        let silosDeliveredTitle = 'Silos effectués';
        let silosDeliveredDetails =
          'Impossible de récupérer les informations concernant les silos effectués';

        if (
          reserve.step.delivery_details[0].items[0].additional_data
            ?.silos_delivered?.length > 0
        ) {
          silosDeliveredTitle = 'Silos effectués';
          silosDeliveredDetails =
            reserve.step.delivery_details[0].items[0].additional_data?.silos_delivered
              ?.map((silo) => `${silo.silo} (${silo.volume_delivered} t.)`)
              ?.join(', ');
        }

        compartmentsAndSilosElements.push(
          <div
            key="silos-delivered"
            className="additional-datas-silos-delivered tw-mb-3"
          >
            <div className="additional-datas-silos-delivered-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
              {silosDeliveredTitle}
            </div>
            <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
              {silosDeliveredDetails}
            </div>
          </div>
        );
      }
    }

    return compartmentsAndSilosElements.length > 0 ? (
      <>{compartmentsAndSilosElements}</>
    ) : null;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      size={'lg'}
      contentClassName="modal-content-reserve"
      dialogClassName="modal-dialog-674px"
      onClick={() => setIsOpen(false)}
    >
      <Modal.Header className="modal-header-reserve" closeButton>
        <div className="claim-header-inner-modal">
          <div className="header-title">
            {returnReserveHeaderTitle(reserve)}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="modal-body-reserve">
        <div>
          <div className="tw-grid tw-grid-cols-2 tw-gap-6">
            <div className="information-section">
              <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
                Informations
              </div>
              <div className="reserve-status tw-mb-3">
                <div className="reserve-status-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                  Statut
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Status
                    status={
                      reserve?.status === 'CREATED'
                        ? STATUS_ENUM.WARN
                        : STATUS_ENUM.POSITIVE
                    }
                    text={
                      reserve_status.properties[reserve?.status]?.display_name
                    }
                  />
                </div>
              </div>

              {reserve?.reserve_reason && (
                <div className="reserve-reason-message tw-mb-3">
                  <div className="reserve-reason-message-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                    Catégorie de réserve
                  </div>
                  <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                    {reserve?.reserve_reason?.reason_message}
                  </div>
                </div>
              )}

              {reserve?.reserve_reason?.criticality && (
                <div className="reserve-criticality tw-mb-3">
                  <div className="reserve-criticality-title tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)] tw-mb-1">
                    Criticité
                  </div>
                  <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.90)]">
                    {returnCriticalityDiv(reserve?.reserve_reason?.criticality)}
                  </div>
                </div>
              )}
            </div>

            {returnDestinataireDivIfAny(reserve)}

            <div className="tw-col-span-2">
              <div className="tw-grid tw-grid-cols-2 tw-gap-6">
                <div>
                  {returnDeliveryDetailsDivOrVoid()}
                  {returnProductsDetailsOrVoid()}
                </div>
                <div>{returnCompartmentsAndSilosDivOrVoid()}</div>
              </div>
            </div>
          </div>

          <div className="tw-mt-6">
            <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
              Description de l'anomalie
            </div>
            {reserve?.message && (
              <div className="tw-flex tw-items-start tw-gap-2 tw-bg-[#ECEEF0] tw-rounded-lg tw-p-2 tw-mb-3">
                <div className="tw-flex-shrink-0 tw-w-[18px] tw-h-[18px] tw-rounded-full tw-bg-[#E53935] tw-flex tw-items-center tw-justify-center tw-text-white tw-font-semibold tw-text-[9px]">
                  {getInitialsDriver(reserve?.driver?.fullname)}
                </div>
                <div className="tw-flex-1">
                  <div className="tw-font-medium tw-font-work tw-text-[13px] tw-text-black tw-mb-1 tw-leading-[1.2]">
                    {reserve?.driver?.fullname}
                  </div>
                  <div className="tw-text-[13px] tw-text-black tw-leading-[1.2] tw-font-work">
                    {reserve?.message}
                  </div>
                  {renderDocuments(reserve?.documents)}
                </div>
              </div>
            )}
            <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
              Réponse
            </div>
            {reserve?.response && (
              <div className="tw-flex tw-items-start tw-gap-2 tw-bg-[#ECEEF0] tw-rounded-lg tw-p-2 tw-mb-3">
                <div className="tw-flex-shrink-0 tw-w-[18px] tw-h-[18px] tw-rounded-full tw-bg-[#E53935] tw-flex tw-items-center tw-justify-center tw-text-white tw-font-semibold tw-text-[9px]">
                  {getInitials(reserve?.user_answered)}
                </div>
                <div>
                  <div className="tw-font-medium tw-font-work tw-text-[13px] tw-text-black tw-mb-1 tw-leading-[1.2]">
                    {reserve?.user_answered?.first_name}{' '}
                    {reserve?.user_answered?.last_name}
                  </div>
                  <div className="tw-text-[13px] tw-text-black tw-leading-[1.2] tw-font-work">
                    {reserve?.response}
                  </div>
                </div>
              </div>
            )}

            {reserve?.status !== 'SOLVED' && (
              <>
                <div className="section tw-mb-1">
                  <textarea
                    onChange={(event) => setComment(event.target.value)}
                    value={comment}
                    className="reserve-comment tw-mt-2"
                    placeholder="Vous pouvez ajouter un commentaire facultatif avant de traiter la réserve..."
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="modal-reserve-footer justify-content-center">
        {error?.length > 0 && <div className="error">{error}</div>}
        <div className="modal-reserve-footer-body">
          <Button
            variant="secondary"
            className="modal-btn-footer"
            disabled={isLoading || reserve?.status === 'SOLVED'}
            onClick={() => {
              putReserveDetails(reserve_id, comment);
            }}
          >
            {reserve?.status !== 'SOLVED'
              ? 'Traiter la réserve'
              : 'Réserve traitée'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default ModalReserveV2;
